import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  InboxOutlined,
  TagOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  IWarehouseLocation,
  IWarehouseLocationDetail,
} from "../../../models/Warehouse";
import {
  useAcceptReturnWarehouse,
  useGetReturnSlipWarehouseDetail,
} from "../../../query/WarehouseQuery";
import { RootState } from "../../../store";
import { notify } from "../../../utils/toast";

const { Text, Title } = Typography;

interface ModalViewReturnSlipProps {
  open: boolean;
  onClose: () => void;
  returnSlipId: number | null;
}

const ModalViewReturnSlip: React.FC<ModalViewReturnSlipProps> = ({
  open,
  onClose,
  returnSlipId,
}) => {
  const warehouseLocations = useSelector(
    (state: RootState) => state.warehouse.locations,
  );

  const [form] = Form.useForm();
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(
    null,
  );

  const { data: returnSlipDetail, isLoading } = useGetReturnSlipWarehouseDetail(
    returnSlipId!,
  );
  const acceptReturnWarehouse = useAcceptReturnWarehouse();

  const getStatusConfig = (
    status: "PENDING" | "APPROVED" | "REJECTED",
  ): { color: string; icon: React.ReactNode; text: string } => {
    switch (status) {
      case "APPROVED":
        return {
          color: "success",
          icon: <CheckCircleOutlined />,
          text: "Đã nhập kho",
        };
      case "REJECTED":
        return {
          color: "error",
          icon: <CloseCircleOutlined />,
          text: "Đã từ chối",
        };
      case "PENDING":
      default:
        return {
          color: "warning",
          icon: <ClockCircleOutlined />,
          text: "Chưa nhập kho",
        };
    }
  };

  const handleConfirm = async () => {
    try {
      const values = await form.validateFields();
      await acceptReturnWarehouse.mutateAsync({
        id: returnSlipId!,
        locationId: values.locationId,
      });
      notify.success("Đã hoàn thành nhập kho");
      onClose();
    } catch (error) {
      notify.error("Có lỗi xảy ra khi xác nhận nhập kho");
    }
  };

  const selectedWarehouseData = warehouseLocations.find(
    (w) => w.id === selectedWarehouse,
  );

  const columns: TableColumnsType = [
    {
      title: "STT",
      key: "index",
      width: 60,
      align: "center",
      render: (_: unknown, __: any, index: number) => index + 1,
    },
    {
      title: "Part Number",
      dataIndex: ["partNumber", "partNumber"],
      key: "partNumber",
      width: 150,
      render: (text: string) => (
        <Text strong className="text-blue-600">
          {text}
        </Text>
      ),
    },
    {
      title: "Mô tả",
      dataIndex: ["partNumber", "description"],
      key: "description",
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <Text type="secondary">{text || "Chưa có mô tả"}</Text>
      ),
    },
    {
      title: "Nhà sản xuất",
      dataIndex: ["partNumber", "manufacturer"],
      key: "manufacturer",
      width: 150,
      ellipsis: true,
    },
    {
      title: "AWB",
      dataIndex: ["partNumber", "awb"],
      key: "awb",
      width: 120,
    },
    {
      title: "Số lượng hoàn trả",
      dataIndex: "returnQuantity",
      key: "returnQuantity",
      width: 130,
      align: "center",
      render: (value: number) => (
        <Text strong style={{ color: "#1890ff", fontSize: 16 }}>
          {value}
        </Text>
      ),
    },
    {
      title: "Đơn vị",
      dataIndex: ["partNumber", "unit"],
      key: "unit",
      width: 80,
      align: "center",
      render: (text: string) => <Tag>{text}</Tag>,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1200}
      footer={null}
      closeIcon={<CloseOutlined />}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FileTextOutlined style={{ fontSize: 20, color: "#1890ff" }} />
          <span>Chi tiết phiếu hoàn trả</span>
        </div>
      }
    >
      <Spin spinning={isLoading}>
        {returnSlipDetail && (
          <div style={{ padding: "16px 0" }}>
            <Card
              style={{ marginBottom: 24 }}
              styles={{ body: { padding: 16 } }}
            >
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Mã phiếu">
                  <Tag color="blue" icon={<TagOutlined />}>
                    #{returnSlipDetail.id}
                  </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Trạng thái">
                  {(() => {
                    const config = getStatusConfig(returnSlipDetail.status);
                    return (
                      <Tag
                        color={config.color}
                        icon={config.icon}
                        style={{ fontSize: 14, padding: "4px 12px" }}
                      >
                        {config.text}
                      </Tag>
                    );
                  })()}
                </Descriptions.Item>

                <Descriptions.Item label="Tên phiếu" span={2}>
                  <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                    {returnSlipDetail.slipName}
                  </Text>
                </Descriptions.Item>

                <Descriptions.Item
                  label="Thời gian tạo"
                  span={
                    returnSlipDetail.status === "APPROVED" &&
                    returnSlipDetail.warehouseLocation
                      ? 2
                      : 2
                  }
                >
                  <Text>
                    {new Date(returnSlipDetail.createdAt).toLocaleString(
                      "vi-VN",
                    )}
                  </Text>
                </Descriptions.Item>

                {returnSlipDetail.status === "APPROVED" &&
                  returnSlipDetail.warehouseLocation && (
                    <>
                      <Descriptions.Item label="Vị trí nhập kho">
                        <Tag
                          color="green"
                          icon={<EnvironmentOutlined />}
                          style={{ fontSize: 14, padding: "4px 12px" }}
                        >
                          {returnSlipDetail.warehouseLocation.code}
                        </Tag>
                      </Descriptions.Item>

                      <Descriptions.Item label="Kho nhập">
                        <Tag
                          color="blue"
                          style={{ fontSize: 14, padding: "4px 12px" }}
                        >
                          {
                            returnSlipDetail.warehouseLocation.warehouse
                              .warehouseCode
                          }
                        </Tag>
                      </Descriptions.Item>
                    </>
                  )}
              </Descriptions>
            </Card>

            {returnSlipDetail.status === "PENDING" && (
              <Card
                title={
                  <div className="flex items-center gap-2">
                    <WarningOutlined className="text-orange-500" />
                    <span>Chọn kho và vị trí nhập hàng hoàn trả</span>
                  </div>
                }
                style={{ marginBottom: 24 }}
                styles={{ body: { padding: 16 } }}
              >
                <Form form={form} layout="vertical">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Chọn kho"
                        name="warehouseId"
                        rules={[
                          { required: true, message: "Vui lòng chọn kho!" },
                        ]}
                      >
                        <Select
                          placeholder="Chọn kho..."
                          onChange={(value) => {
                            setSelectedWarehouse(value);
                            form.setFieldValue("locationId", undefined);
                          }}
                          options={warehouseLocations.map(
                            (warehouse: IWarehouseLocation) => ({
                              label: `${warehouse.warehouseCode}`,
                              value: warehouse.id,
                            }),
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Chọn vị trí"
                        name="locationId"
                        rules={[
                          { required: true, message: "Vui lòng chọn vị trí!" },
                        ]}
                      >
                        <Select
                          placeholder="Chọn vị trí..."
                          disabled={!selectedWarehouse}
                          options={
                            selectedWarehouseData?.warehouseLocations
                              ?.filter(
                                (loc: IWarehouseLocationDetail) => loc.isActive,
                              )
                              .map((location: IWarehouseLocationDetail) => ({
                                label: `${location.code}`,
                                value: location.id,
                              })) || []
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleConfirm}
                    size="large"
                    block
                  >
                    Xác nhận nhập hàng hoàn trả
                  </Button>
                </Form>
              </Card>
            )}

            <Card
              title={
                <Title level={5} style={{ margin: 0 }}>
                  <InboxOutlined className="mr-2" />
                  Danh sách linh kiện hoàn trả
                </Title>
              }
              styles={{ body: { padding: 0 } }}
            >
              <Table
                columns={columns}
                dataSource={returnSlipDetail.partNumberReturnSlipDetails}
                rowKey="id"
                pagination={false}
                scroll={{ x: 1000, y: 400 }}
                size="small"
              />
            </Card>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ModalViewReturnSlip;
